import get from "lodash/get";

/* eslint-disable max-lines-per-function */
import LoginProxy from "../proxies/back-end/authentication/login-proxy";
import UpdateUserProxy from "../proxies/back-end/users/update";
import GetCompanyUserProxy from "../proxies/back-end/company-user/get";
import { switchCompanyToSleekSign } from "./switch-company-to-sleek-sign";
import GetSleekSignUserDetailByEmailProxy from "../proxies/back-end/sleeksign/get-user-details-by-email";
import { getAppFeatureProp } from "@/utilities/app-feature-util";
import { getAppcuesLoader, identifyUser } from "@/utilities/appcues-util";
import { initChameleon } from "@/utilities/chameleon-util";

import { Constants } from "customer-sdk/constants";

const SLEEK_SIGN_URL = Constants.SLEEK_SIGN_URLS.SLEEK_SIGN_URL;
const SLEEK_APP_CACHE_KEY = Constants.STORE_CONSTANTS.SLEEK_APP_CACHE_KEY;
const { COMPANY_USER_MODULE, SET_COMPANY_USERS } = Constants.COMPANY_USERS_CONSTANTS;
//#region Private Methods
let parseCompaniesJson = (input) => {
  try {
    let output = JSON.parse(input);

    if (output instanceof Array) {
      return output;
    }
  } catch (error) {
    console.log("Bad structure of cached companies...");
  }

  return [];
};

const sleekSignUserDetailsProxy = async ({ authToken, email }) => {
  let proxy = new GetSleekSignUserDetailByEmailProxy();
  return await proxy.getByEmail({
    authToken,
    email,
  });
};

let getCompanyUsers = async (id) => {
  let proxy = new GetCompanyUserProxy();
  let store = new LocalStoreManager();

  return await proxy.execute({ authToken: store.getToken(), id: id });
};

const isSleekSignEnabled = () => {
  const sleekAppCache = JSON.parse(localStorage.getItem(SLEEK_APP_CACHE_KEY));
  const appFeatures = get(sleekAppCache, "configModule.platformConfig.cmsAppFeatures", []);
  const sleek_sign = getAppFeatureProp(appFeatures, "sleek_sign") || {};

  return sleek_sign.enabled || false;
};
//#endregion

/**
 * @class LocalStoreManager
 * @description Helper used for managing data stored in local storage.
 * @author Allan A. Chua
 * @version 1.0
 * @since November 21, 2018
 */
export default class LocalStoreManager {
  /**
   * @function storeProfile
   * @description Method used for storing user profile in local store.
   * @param {Object} input Profile instance.
   * @param {String} input.authToken Authentication token used for retrieving user data.
   */
  // eslint-disable-next-line complexity
  async storeProfile(component, input = {}) {
    if (input) {
      const {
        authToken,
        userId,
        email,
        phone,
        firstName,
        lastName,
        isAdmin,
        isSuperAdmin,
        isItSupport,
        isBookkeeping,
        isBookkeepingAdmin,
        isAccountingAdmin,
        isAccounting,
        isEmailVerified,
        companies,
        isPartner,
        isPartnerAdmin,
        partner,
        lastAccessedCompanyId,
      } = input;

      localStorage.setItem("authToken", authToken);
      localStorage.setItem("userId", userId);
      localStorage.setItem("email", email);
      localStorage.setItem("phone", phone);
      localStorage.setItem("firstName", firstName);
      localStorage.setItem("lastName", lastName);
      localStorage.setItem("isAdmin", isAdmin);
      localStorage.setItem("isSleekAdmin", isAdmin);
      localStorage.setItem("isSuperAdmin", isSuperAdmin);
      localStorage.setItem("isItSupport", isItSupport);
      localStorage.setItem("isBookkeeping", isBookkeeping);
      localStorage.setItem("isBookkeepingAdmin", isBookkeepingAdmin);
      localStorage.setItem("isAccountingAdmin", isAccountingAdmin);
      localStorage.setItem("isAccounting", isAccounting);
      localStorage.setItem("isEmailVerified", isEmailVerified);
      localStorage.setItem("companies", companies);
      localStorage.setItem("isPartner", isPartner ? isPartner : false);
      localStorage.setItem("isPartnerAdmin", isPartnerAdmin ? isPartnerAdmin : false);
      localStorage.setItem("partner", partner ? partner : null);
      localStorage.setItem("initializedViaLogin", true);
      localStorage.setItem("sbaNotAvailableModalAlreadyShow", false);
      localStorage.setItem("sbaNotificationAlreadyShow", false);

      if (lastAccessedCompanyId) {
        try {
          if (component) {
            let companyUsers = await getCompanyUsers(lastAccessedCompanyId);
            component.$store.dispatch(`${COMPANY_USER_MODULE}/${SET_COMPANY_USERS}`, companyUsers);
          }
        } catch (error) {
          throw error;
        }
        localStorage.setItem("companyId", lastAccessedCompanyId);
      }

      if (isSleekSignEnabled() && authToken) {
        try {
          const { data, error } = await sleekSignUserDetailsProxy({
            authToken,
          });
          if (data) {
            localStorage.setItem("sleekSignUserDetail", JSON.stringify(data));
          } else throw new Error(error.message);
        } catch (error) {
          localStorage.setItem("sleekSignUserDetail", {});
          console.error("Cannot fetch sleekSignUserDetailsProxy");
          console.error({ error });
        }
      }

      // initiate Appcues for tour guide
      try {
        const appcues = await getAppcuesLoader();
        identifyUser(appcues);
      } catch (error) {
        console.error(`Error occurred @local-store-manager.js:storeProfile ${error}`);
      }

      // initiate chameleon for product tour
      try {
        await initChameleon();
      } catch (error) {
        console.error(`Error occurred in chameleon @local-store-manager.js:storeProfile ${error}`);
      }
    }
  }
  /**
   * @function getItem
   * @description Method used for retriving an item from local store.
   * @param {String} key
   */
  async getItem(key) {
    const authToken = localStorage.getItem("authToken");
    const value = localStorage.getItem(key);

    if (authToken) {
      if (value && value !== "undefined" && typeof value !== "undefined") {
        return value;
      }

      let proxy = new LoginProxy();
      let response = await proxy.getUserContext(authToken);

      if (response) {
        let profile = response.data || response;
        this.storeProfile(null, profile);

        return localStorage.getItem(key);
      }
    }

    return null;
  }
  /**
   * @function setItem
   * @description Method used for retriving an item from local store.
   * @param {String} key
   * @param {String} value
   */
  async setItem(key, value) {
    const authToken = localStorage.getItem("authToken");

    if (authToken && value !== null && value !== undefined) {
      if (value && value !== "undefined" && typeof value !== "undefined") {
        localStorage.setItem(key, JSON.stringify(value));
      }
    }
  }

  /**
   * @function removeItem
   * @description Method used for removing an item from local store.
   * @param {String} key
   */
  removeItem(key) {
    localStorage.removeItem(key);
  }

  /**
   * @async
   * @function checkIfEmailIsVerified
   * @description Method used for checking if currently logged-in user has a verified email.
   * @returns {Promise<Boolean>} Flag that indicates if user's email is verified.
   */
  async checkIfEmailIsVerified() {
    let isEmailVerified = await this.getItem("isEmailVerified");

    return isEmailVerified === "true";
  }
  /**
   * @async
   * @function fetchAccountData
   * @description Method used for retrieving account data from authentication token.
   */
  async fetchAccountData(component) {
    const authToken = localStorage.getItem("authToken");

    if (authToken) {
      let proxy = new LoginProxy();
      let response = await proxy.getUserContext(authToken);

      if (response) {
        let profile = response.data || response;
        await this.storeProfile(component, profile);
      }
    }
  }
  /**
   * @function getToken
   * @description Method used for retrieving the current user's authentication token.
   * @returns {String} Authentication token.
   */
  getToken() {
    return localStorage.getItem("authToken");
  }
  /**
   * @function getUserFirstName
   * @description Method used for retrieving user first name.
   * @returns {String} User first name.
   */
  getUserFirstName() {
    return localStorage.getItem("firstName");
  }

  /**
   * @function getUserFullName
   * @description Method used for retrieving user first name.
   * @returns {String} User first name.
   */
  getUserFullName() {
    return `${localStorage.getItem("firstName")} ${localStorage.getItem("lastName")}`;
  }
  /**
   * @function getUserLastName
   * @description Method used for retrieving user last name.
   * @returns {String} User last name.
   */
  getUserLastName() {
    return localStorage.getItem("lastName");
  }
  /**
   * @function setUserFirstName
   * @description Method used for setting user first name.
   * @returns {String} User first name.
   */
  setUserFirstName(firstName) {
    localStorage.setItem("firstName", firstName);
  }
  /**
   * @function setUserLastName
   * @description Method used for setting user last name.
   * @returns {String} User last name.
   */
  setUserLastName(lastName) {
    localStorage.setItem("lastName", lastName);
  }
  /**
   * @function setUserEmail
   * @description Method used for setting user email.
   * @returns {String} User email.
   */
  setUserEmail(email) {
    localStorage.setItem("email", email);
  }
  /**
   * @function getUserEmail
   * @description Method used for retrieving user email.
   * @returns {String} User email.
   */
  getUserEmail() {
    return localStorage.getItem("email");
  }
  /**
   * @function setUserPhone
   * @description Method used for setting user phone.
   * @returns {String} User phone.
   */
  setUserPhone(phone) {
    localStorage.setItem("phone", phone);
  }
  /**
   * @function getUserPhone
   * @description Method used for retrieving user phone.
   * @returns {String} User phone.
   */
  getUserPhone() {
    return localStorage.getItem("phone");
  }
  /**
   * @function getUserId
   * @description Method used for retrieving user id.
   * @returns {String} User id.
   */
  getUserId() {
    return localStorage.getItem("userId");
  }

  getIsAdmin() {
    return localStorage.getItem("isAdmin") === "true";
  }

  getIsSuperAdmin() {
    return localStorage.getItem("isSuperAdmin") === "true";
  }

  getIsItSupport() {
    return localStorage.getItem("isItSupport") === "true";
  }

  getIsBookkeeping() {
    return localStorage.getItem("isBookkeeping") === "true";
  }

  getIsBookkeepingAdmin() {
    return localStorage.getItem("isBookkeepingAdmin") === "true";
  }

  getIsAccountingAdmin() {
    return localStorage.getItem("isAccountingAdmin") === "true";
  }

  getIsAccounting() {
    return localStorage.getItem("isAccounting") === "true";
  }

  getIsCorpSecAdmin() {
    return localStorage.getItem("isCorpSecAdmin") === "true";
  }

  setIsCorpSecAdmin(value) {
    localStorage.setItem("isCorpSecAdmin", value);
  }

  getSleekSignUserDetails() {
    const details = localStorage.getItem("sleekSignUserDetail");

    try {
      return JSON.parse(details);
    } catch (error) {
      return null;
    }
  }

  setSleekSignUserDetails(details) {
    localStorage.setItem("sleekSignUserDetail", JSON.stringify(details));
  }

  setIsInappTourOpened(value) {
    localStorage.setItem("isInappTourOpened", value);
  }

  getIsInappTourOpened() {
    return localStorage.getItem("isInappTourOpened");
  }

  /**
   * Is current user a sleek admin
   * @returns {boolean} true if sleek admin
   */
  getIsSleekAdmin() {
    return localStorage.getItem("isSleekAdmin") === "true";
  }
  /**
   * @async
   * @function getCompanyId
   * @description Method used for retrieving company ID from user.
   * @returns {String} Company ID.
   */
  getCompanyId() {
    let urlString = window.location.href;
    let url = new URL(urlString);
    let cid = url.searchParams.get("cid");
    if (cid !== null) {
      localStorage.setItem("companyId", cid);
      // localStorage.setItem("isSleekAdmin", "true");
      return cid;
    }

    return localStorage.getItem("companyId");
  }
  /**
   * @function setToken
   * @description Method used for setting the current context's token.
   * @param {String} token Authentication token.
   */
  setToken(token) {
    localStorage.setItem("authToken", token);
  }
  /**
   * @function setCompanyId
   * @description Method used for setting the ID of the currently selected company ID.
   * @param {String} id ID of currently selected company.
   */
  setCompanyId(id) {
    localStorage.setItem("companyId", id);
    switchCompanyToSleekSign(SLEEK_SIGN_URL[window.location.host], id);
    this.setLastAccessedCompanyId(id);
  }

  setCompanyStatus(status) {
    localStorage.setItem("companyStatus", status);
  }

  getCompanyStatus() {
    return localStorage.getItem("companyStatus");
  }

  setCompanySubStatus(status) {
    localStorage.setItem("companySubStatus", status);
  }

  getCompanySubStatus() {
    return localStorage.getItem("companySubStatus");
  }

  setNeedsToPayForCompany(needsToPay) {
    if (needsToPay) {
      localStorage.setItem("needsToPay", "true");
    } else {
      localStorage.setItem("needsToPay", "false");
    }
  }

  setMicroserviceEnable(microserviceEnable) {
    if (microserviceEnable) {
      localStorage.setItem("microserviceEnable", microserviceEnable);
    } else {
      localStorage.removeItem("microserviceEnable");
    }
  }

  getMicroserviceEnable() {
    return localStorage.getItem("microserviceEnable");
  }

  getNeedsToPayForCompany() {
    return localStorage.getItem("needsToPay") === "true";
  }

  /**
   * @function setLastAccessedCompanyId
   * @description Method used for updating user's last accessed company's id in database.
   * @param {String} companyId ID of currently selected company.
   */
  async setLastAccessedCompanyId(companyId) {
    try {
      let proxy = new UpdateUserProxy();
      let userId = this.getUserId();
      if (!userId) {
        return;
      }
      await proxy.execute(userId, {
        last_accessed_company_id: companyId,
      });
    } catch (error) {
      throw error;
    }
  }
  /**
   * @function updateCompanyInCache
   * @description Method used for caching newly added companies to local store.
   * @param {Object} company - Company instance.
   * @param {String} company.id - ID of company.
   * @param {String} company.name - Name of company.
   * @param {String} company.uen - UEN of company.
   * @param {String} company.status - Status of company.
   * @param {Boolean} company.isOwner - Flag that indicates if currently logged-in user is owner.
   * @param {Boolean} company.isDirector - Flag that indicates if currently logged-in user is director.
   * @param {Boolean} company.isShareholder - Flag that indicates if currently logged-in user is shareholder.
   */
  updateCompanyInCache(company = {}) {
    let jsonCompanies = localStorage.getItem("companies");
    let companies = parseCompaniesJson(jsonCompanies);
    companies = companies.filter((item) => item.id !== company.id);

    companies.push(company);

    localStorage.setItem("companies", JSON.stringify(companies));
  }
  /**
   * @function logout
   * @description Method used for clearing local store.
   */
  logout() {
    const accountingSetupPageAccessor = localStorage.getItem("accountingSetupPage");
    const isSleekSignPopupHidden = localStorage.getItem("isSleekSignPopupHidden");
    const isSleekSignTemplateWelcomePanelHidden = localStorage.getItem("isSleekSignTemplateWelcomePanelHidden");
    localStorage.clear();
    if (accountingSetupPageAccessor) {
      this.setAccountingSetupPageAccessor(accountingSetupPageAccessor);
    }
    if (isSleekSignPopupHidden) {
      this.hideSleekSignPopup();
    }
    if (isSleekSignTemplateWelcomePanelHidden) {
      this.hideSleekSignTemplateWelcomePanel();
    }
  }
  /**
   * @function verifyEmail
   * @description Method used for marking the user's email verification status.
   */
  verifyEmail() {
    localStorage.setItem("isEmailVerified", true);
  }

  setInvitationToken(invitationToken) {
    localStorage.setItem("invitationToken", invitationToken);
  }

  getInvitationToken() {
    localStorage.getItem("invitationToken");
  }

  setLastKnownBuildVersion(buildVersion) {
    sessionStorage.setItem("lastKnownBuildVersion", buildVersion);
  }

  getLastKnownBuildVersion() {
    sessionStorage.getItem("lastKnownBuildVersion");
  }

  setPartnerId(partnerId) {
    localStorage.setItem("partner", partnerId);
  }

  getPartnerId() {
    return localStorage.getItem("partner");
  }

  setPaymentEntryPoint(entryPoint) {
    localStorage.setItem("payment_entry_point", entryPoint);
  }

  getPaymentEntryPoint() {
    return localStorage.getItem("payment_entry_point");
  }

  getUserData() {
    let user = JSON.parse(localStorage.getItem("user"));
    let userFormData = JSON.parse(localStorage.getItem("userFormData"));
    return {
      user,
      userFormData,
    };
  }

  getIsTwoFactor() {
    return localStorage.getItem("isTwoFactor") === "true";
  }

  getIsLogin() {
    return localStorage.getItem("isLogin") === "true";
  }

  getPhoneData() {
    let phoneData = JSON.parse(localStorage.getItem("phoneData"));
    return phoneData;
  }

  setAccountingSetupPageAccessor(companyId) {
    localStorage.setItem("accountingSetupPage", companyId);
  }

  resetAccountingSetupPageAccessor() {
    localStorage.removeItem("accountingSetupPage");
  }

  isAccountingSetupPageAccessed(companyId) {
    const lastCompanyId = localStorage.getItem("accountingSetupPage");
    return lastCompanyId === companyId;
  }

  setInitializedViaLogin(input) {
    localStorage.setItem("initializedViaLogin", input);
  }

  setBusinessAccountToken(token) {
    localStorage.setItem("businessAccountToken", token);
  }

  getBusinessAccountToken() {
    return localStorage.getItem("businessAccountToken");
  }

  isSleekSignPopupHidden() {
    return localStorage.getItem("isSleekSignPopupHidden") === "true";
  }

  hideSleekSignPopup() {
    localStorage.setItem("isSleekSignPopupHidden", true);
  }

  isSleekSignTemplateWelcomePanelHidden() {
    return localStorage.getItem("isSleekSignTemplateWelcomePanelHidden") === "true";
  }

  hideSleekSignTemplateWelcomePanel() {
    localStorage.setItem("isSleekSignTemplateWelcomePanelHidden", true);
  }

  setNewCurrencyCode(code) {
    if (!code) {
      localStorage.removeItem("companyIdForNewCurrency");
      return localStorage.removeItem("newCurrencyCode");
    }
    localStorage.setItem("newCurrencyCode", code);
  }

  getNewCurrencyCode() {
    return localStorage.getItem("newCurrencyCode");
  }

  setCompanyIdForNewCurrency(companyId) {
    localStorage.setItem("companyIdForNewCurrency", companyId);
  }

  getCompanyIdForNewCurrency() {
    return localStorage.getItem("companyIdForNewCurrency");
  }

  setIsPersonalInformationStepInProgress(value) {
    if (!value) {
      localStorage.removeItem("isPersonalInformationInProgress");
    }
    localStorage.setItem("isPersonalInformationInProgress", value);
  }

  getIsPersonalInformationStepInProgress() {
    return localStorage.getItem("isPersonalInformationInProgress") === "true";
  }

  enableSbaSkip2faFirstAttempt() {
    localStorage.setItem("sba_skip_2fa_first_attempt", true);
  }

  disableSbaSkip2faFirstAttempt() {
    localStorage.removeItem("sba_skip_2fa_first_attempt");
  }

  isSbaSkip2faFirstAttemptEnabled() {
    return localStorage.getItem("sba_skip_2fa_first_attempt");
  }

  getAuthKey() {
    return localStorage.getItem("auth_key");
  }

  setAuthKey(value) {
    return localStorage.setItem("auth_key", value);
  }

  clearAuthKey() {
    localStorage.removeItem("auth_key");
  }

  savePaymentRequestPeriod(processingPeriod) {
    localStorage.setItem("paymentRequestPeriod", processingPeriod);
  }

  saveTempZendeskTicket(newTicket) {
    const data = localStorage.getItem("zendeskTickets") || "[]";
    const tickets = JSON.parse(data);
    // console.log(tickets);
    const FIVE_MINUTES_IN_MILLISECONDS = 5 * 60 * 1000;
    const now = new Date().getTime();
    const newTickets = tickets.filter((ticket) => now - ticket.time < FIVE_MINUTES_IN_MILLISECONDS);
    const existTicket = newTickets.find((ticket) => ticket.id == newTicket.id);
    if (existTicket) {
      existTicket.time = newTicket.time;
      existTicket.action = newTicket.action;
    } else {
      newTickets.push(newTicket);
    }
    localStorage.setItem("zendeskTickets", JSON.stringify(newTickets));
    // console.log({ newTickets });
  }

  getTempZendeskTickets(companyId, userEmail, action) {
    const data = localStorage.getItem("zendeskTickets") || "[]";
    const tickets = JSON.parse(data);
    // console.log(tickets);
    const FIVE_MINUTES_IN_MILLISECONDS = 5 * 60 * 1000;
    const now = new Date().getTime();
    const newTickets = tickets.filter((ticket) => now - ticket.time < FIVE_MINUTES_IN_MILLISECONDS);
    localStorage.setItem("zendeskTickets", JSON.stringify(newTickets));
    return newTickets.filter(
      (ticket) =>
        ticket.companyId == companyId && ticket.userEmail == userEmail && (action ? ticket.action == action : true)
    );
  }

  setCoaCategory(coa) {
    localStorage.setItem("coaCategory", JSON.stringify(coa));
  }

  getCoaCategory() {
    return localStorage.getItem("coaCategory");
  }
}
